import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RadioIconContainer } from './DetailedRadioIconField.styled';
import {
  filterTraining,
  filterTrainings,
  getTrainingsByDomain,
} from '@actions/formulary';
import DetailedRadioIconOption from './DetailedRadioIconOption';
import { useMultiStepContext } from '@components/hoc/MultiStepForm/MultiStepFormContext';
import { Spinner } from '@components/atoms/AnimatedElements/AnimatedElements.styled';
import { overridesTrainingNames } from '@config/lists';
import { TrainingTypes } from '@config/enums/FormationTypeEnum';

const DetailedRadioIconField = ({
  contextValue,
  dataSet,
  handleFormData,
  handleEvent,
  id,
  domainId,
  bgColorDefault = 'white',
  bgColorSelect = '#E2F8F5',
  textColorDefault = 'black',
  textColorSelect = 'black',
}) => {
  const { trainingNameId, visibleSteps, setConfig, setTrainingInfos } = useMultiStepContext();
  const [dataState, setDataState] = useState(null);
  const [selectedItem, setSelectedItem] = useState(contextValue || null);

  const handleFieldChange = (value, selectedItem, nameId = 0) => {
    if (value) {
      setSelectedItem(value);
      // On vérifie si on avait pas enlevé l'étape 6, si oui on la réintroduit dans les étapes visibles
      let newVisibleSteps = [...visibleSteps];
      if (
        newVisibleSteps.findIndex((elem) => elem.key === 'niveauEtudes') === -1
      ) {
        let indexOfFormation = visibleSteps.findIndex(
          (elem) => elem.key === 'training'
        );
        newVisibleSteps.splice(indexOfFormation + 1, 0, { index: 5, key: 'niveauEtudes' });
      }
      setConfig({
        visibleSteps: newVisibleSteps,
        maxStep: newVisibleSteps.length,
      });
    }

    if (value !== 'nothing') {
      selectedItem.type = filterTraining(selectedItem);
      selectedItem.trainingNameId = nameId;
      setTrainingInfos(selectedItem);
    } else {
      setTrainingInfos(null);
    }

    handleFormData({ name: id, value });
    if (typeof handleEvent === 'function') {
      handleEvent();
    }
  };

  const handleTrainingStepQuick = (data) => {
    handleFormData({ name: id, value: data[0].ID_FORMATION_BITRIX });
    setTrainingInfos(data[0]);
    if (typeof handleEvent === 'function') {
      handleEvent();
    }
  };

  const renderDefaultOption = (option) => {
    const commonProps = {
      style: {
        backgroundColor:
          option.ID_FORMATION_BITRIX === Number(selectedItem)
            ? bgColorSelect
            : bgColorDefault,
        color:
          option.ID_FORMATION_BITRIX === Number(selectedItem)
            ? textColorSelect
            : textColorDefault,
      },
      eventHandler: (event) => {
        handleFieldChange(event.target.value, option);
      },
      icon: option.ID_FORMATION_BITRIX,
      name: overridesTrainingNames[option.ID_FORMATION_BITRIX]
        ? overridesTrainingNames[option.ID_FORMATION_BITRIX].name
        : option.id,
      id: option.ID_FORMATION_BITRIX,
      value: option.ID_FORMATION_BITRIX,
      checked: option.ID_FORMATION_BITRIX === Number(selectedItem),
      timestamp: option.PERIODE,
      trainingType: option.trainingType
    };

    return <DetailedRadioIconOption key={option.id} {...commonProps} />;
  };

  const renderOption = (option, elem) => {
    const commonProps = {
      style: {
        backgroundColor:
          option.ID_FORMATION_BITRIX + '_' + elem.id ===
          `${selectedItem}_${trainingNameId}`
            ? bgColorSelect
            : bgColorDefault,
        color:
          option.ID_FORMATION_BITRIX + '_' + elem.id ===
          `${selectedItem}_${trainingNameId}`
            ? textColorSelect
            : textColorDefault,
      },
      eventHandler: (event) => {
        handleFieldChange(event.target.value, option, elem.id);
      },
      icon: elem.id === 1 ? option.ID_FORMATION_BITRIX : option.ID_FORMATION_BITRIX + '_' + elem.id,
      name: elem.name,
      id: option.ID_FORMATION_BITRIX + '_' + elem.id,
      value: option.ID_FORMATION_BITRIX,
      checked: option.ID_FORMATION_BITRIX + '_' + elem.id === `${selectedItem}_${elem.id}`,
      timestamp: option.PERIODE,
      trainingType: option.trainingType
    };

    return <DetailedRadioIconOption key={`${option.ID_FORMATION_BITRIX}_${elem.id}`} {...commonProps} />;
  };

  const displayRadioIconData = () => {
    if (dataSet !== 'trainings') {
      return null;
    }

    return (
      <>
        {Object
          .entries(TrainingTypes)
          .map(([trainingType, trainingLabel]) => {
            const trainingData = dataState[trainingType];

            if (trainingData && trainingData.length > 0) {
              return (
                <React.Fragment key={trainingType}>
                  {trainingData
                    .sort((a, b) => a.position - b.position)
                    .map((option) => {
                        // Ajout du type de formation dans les options pour récupérer le tag
                        option.trainingType = trainingLabel;

                        return Array.isArray(overridesTrainingNames[option.ID_FORMATION_BITRIX])
                          ? overridesTrainingNames[option.ID_FORMATION_BITRIX].map((elem) =>
                            renderOption(option, elem)
                          )
                          : renderDefaultOption(option)
                      }
                    )}
                </React.Fragment>
              );
            }
            return null;
          })}
      </>
    )
  };

  const availableDataSets = {
    trainings: {
      callback: getTrainingsByDomain,
      filtering: filterTrainings,
    },
  };

  const fetchDataByDomain = (dataSet, domainId) => {
    availableDataSets[dataSet]
      .callback(domainId)
      .then((data) => {
        setDataState(data);
        if (data?.length > 1 || (data?.length === 1 && Array.isArray(overridesTrainingNames[data[0].ID_FORMATION_BITRIX]))) {
          setDataState(availableDataSets[dataSet].filtering(data));
        } else if (data?.length === 1 && selectedItem !== data[0].ID_FORMATION_BITRIX) {
          handleTrainingStepQuick(data);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  useEffect(() => {
    fetchDataByDomain(dataSet, domainId);
  }, []);

  return (
    <RadioIconContainer>
      {dataState ? displayRadioIconData() : <Spinner />}
      <DetailedRadioIconOption
        key={'nothing'}
        style={
          'nothing' === selectedItem
            ? { backgroundColor: bgColorSelect, color: textColorSelect }
            : { backgroundColor: bgColorDefault, color: textColorDefault }
        }
        eventHandler={(event) => {
          handleFieldChange(event.target.value);
        }}
        name={'Je souhaite être conseillé(e)'}
        id={'nothing'}
        value={'nothing'}
        checked={'nothing' === selectedItem}
      />
    </RadioIconContainer>
  );
};

DetailedRadioIconField.propTypes = {
  handleFormData: PropTypes.func.isRequired,
  handleEvent: PropTypes.func,
  contextValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.array,
  bgColorDefault: PropTypes.string,
  bgColorSelect: PropTypes.string,
  textColorDefault: PropTypes.string,
  textColorSelect: PropTypes.string,
};
export default DetailedRadioIconField;
